import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '../../../components/Button';
import { Icon } from '../../../components/Icon';
import { TextArea } from '../../../components/TextArea';
import { TextField } from '../../../components/TextField';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import qs from 'qs';

import { useCreatePopUpForm } from '../../../queries/popupFroms';
import { CreatePopUpForm } from '../../../types/popupForm';
import { usePopUpTargetBranch } from '../../../queries/popupTargetBranch';

export const PopUpFormPage = () => {
  const { goBack, location } = useHistory();
  const { createPopUpForm } = useCreatePopUpForm();

  const queryParams = qs.parse(location.search.replace('?', ''));
  const branchId = queryParams.branchId as string | undefined;
  const { targetBranch } = usePopUpTargetBranch(branchId ?? null);

  const CreatePopUpFormSchema = Yup.object().shape({
    companyName: Yup.string().required('회사명을 입력해주세요.'),
    brandName: Yup.string().required('브랜드명을 입력해주세요.'),
    managerName: Yup.string().required('담당자명을 입력해주세요.'),
    managerPhoneNumber: Yup.string()
      .required('담당자 연락처를 입력해주세요.')
      .matches(/^\d{2,3}-?\d{3,4}-?\d{4}$/, '전화번호 양식이 아닙니다.'),
    desiredBranch: Yup.string().required('희망지점을 입력해주세요.'),
    contents: Yup.string().required('팝업 문의 내용을 입력해주세요.'),
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreatePopUpForm>({
    mode: 'onChange',
    resolver: yupResolver(CreatePopUpFormSchema),
    defaultValues: {
      desiredBranch: targetBranch?.branchName ?? '',
    },
  });

  useEffect(() => {
    if (targetBranch?.branchName) {
      setValue('desiredBranch', targetBranch.branchName);
    }
  }, [targetBranch, setValue]);

  return (
    <div className="md:mx-auto md:mt-8 md:max-w-3xl">
      <div className="ml-5 flex gap-5">
        <button onClick={goBack} className="">
          <Icon.ChevronLeft />
        </button>
        <h4>팝업스토어 문의하기</h4>
      </div>

      <form onSubmit={handleSubmit((data) => createPopUpForm(data))}>
        <div className="mx-5 mt-5 space-y-5 rounded-xl p-4 shadow md:mx-0">
          <p className="text-17 font-bold text-black">정보입력</p>
          <div className="grid-cols-2 space-y-5 md:grid md:gap-5 md:space-y-0">
            <TextField
              placeholder="회사명 (필수)"
              peerStyle={false}
              helper={errors.companyName?.message}
              {...register('companyName', {
                required: '회사명을 입력해주세요.',
              })}
            />
            <TextField
              placeholder="브랜드명 (필수)"
              peerStyle={false}
              helper={errors.brandName?.message}
              {...register('brandName', {
                required: '브랜드명을 입력해주세요.',
              })}
            />
            <TextField
              placeholder="담당자명 (필수)"
              peerStyle={false}
              helper={errors.managerName?.message}
              {...register('managerName', {
                required: '담당자명을 입력해주세요.',
              })}
            />
            <TextField
              placeholder="담당자연락처 (필수)"
              peerStyle={false}
              helper={errors.managerPhoneNumber?.message}
              {...register('managerPhoneNumber', {
                required: '담당자 연락처를 입력해주세요.',
              })}
            />
            <TextField
              type="email"
              placeholder="담당자 이메일"
              peerStyle={false}
              {...register('managerEmail')}
            />
            <TextField
              placeholder="희망지점 (필수)"
              peerStyle={false}
              readOnly={!!branchId}
              className={branchId ? 'bg-gray-100' : ''}
              {...register('desiredBranch', {
                required: '희망지점을 입력해주세요.',
              })}
            />
            <div className="col-span-2">
              <TextArea
                placeholder="내용 (필수)"
                className="h-80 resize-none rounded-lg focus:border-black"
                helper={errors.contents?.message}
                {...register('contents', {
                  required: '팝업스토어 문의 내용을 입력해주세요.',
                })}
              />
            </div>
          </div>
        </div>
        <div className="mt-5 bg-[#FBFAF9] p-5">
          <p className="text-12 font-bold text-[#221F20]">진행 절차</p>
          <p className="mt-1.5 text-12 text-gray-600">
            팝업 신청서 작성
            <br />
            1. 담당자 검토 및 연락 (담당자가 검토 후에 입점이 적합한 경우 전화나
            이메일로 연락을 드리겠습니다.)
            <br />
            2. 조건 협의 및 계약체결 (제휴 내용 구체화 및 조건을 협의한 후
            계약을 체결합니다.)
          </p>

          <p className="mt-5 text-12 font-bold text-[#221F20]">
            ※ 진행 시 주의사항 안내
          </p>
          <p className="mt-1.5 text-12 text-gray-600">
            -아크앤북은 정품을 판매하고 취급하는 업체 및 브랜드만 입점이
            가능합니다.
            <br />
            -입점 신청서에 기재해주신 내용을 내부 검토 후에 입점 가능 여부와
            계약 절차를 안내해드립니다.
            <br />
            -아크앤북은 정품을 판매하고 취급하는 업체 및 브랜드만 입점이
            가능하며, 가품, 도매 사입품(택갈이) 등 아크앤북 사업 방향에 맞지
            않는 브랜드의 입점은 진행하지 않습니다. (병행수입 상품의 경우
            수입신고 필증 보유 시 가능)
          </p>
          <p className="mt-5 text-12 text-gray-600">
            아크앤북에 관심을 가져주시고 좋은 제안 주신 점 감사 말씀 드립니다.
            <br />
            제안 주신 내용은 내부 기준에 따라 검토 예정입니다.
            <br />
            진행이 어렵다고 판단될 경우 별도의 회신을 드리지 않는 점 깊은 양해
            부탁드립니다.
            <br />
            감사합니다.
          </p>
        </div>
        <Button
          text="문의하기"
          className="fixed bottom-0 w-full rounded-none bg-[#221F20] text-center text-white disabled:bg-[#D6D6D6] disabled:text-white md:relative md:mt-6 md:rounded-xl"
          disabled={
            !watch('companyName') ||
            !watch('brandName') ||
            !watch('managerName') ||
            !watch('managerPhoneNumber') ||
            !watch('desiredBranch') ||
            !watch('contents')
          }
        />
      </form>
    </div>
  );
};
